import { deDE, enUS, frFR } from '@mui/x-data-grid/locales';
import { DateTime } from 'luxon';
import { Dispatch, MutableRefObject, useCallback, useRef, useState } from 'react';
import i18n from './i18n';

export function toggle(state: boolean, setter: (newState: boolean) => unknown): () => void {
  return () => {
    setter(!state);
  };
}

export function clipToRange(value: number, min?: number, max?: number): number {
  value = min === undefined ? value : Math.max(value, min); // clip min
  value = max === undefined ? value : Math.min(value, max); // clip max
  return value;
}

export function skipUndefined<T, N>(value: T | undefined, fn: (arg: T) => N): N | undefined {
  return value === undefined || value === null ? undefined : fn(value);
}

export function deepEqual(a?: any, b?: any): boolean {
  // do deep compare without JSON.stringify to avoid issues with different order of keys
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * hook for using state that tracks updates during the current render cycle.
 * This can be used for situations where 2 handlers are calld during the same render cycle and the 2nd handler depends
 * on state updates that were written by the first on.
 * Example (problem):
 * handler A writes myState
 * handler B uses myState to do smth.
 *
 * Problem: B is closed with the value of myState at the time of rendering the component. When handler A is called,
 *  that myState is lined up for an update in the next render cycle but B does not know about that. Now when B gets
 *  called within the same render cycle, it will work on the "old" value of myState (the value before the update by
 *  A).
 *
 * This hook provides a syncronously updated state along with the traditional state.
 * Usage:
 * [myState, myStateSync, setMyState] = useSyncState<number>(42);
 * useCallback(() => {
 *  console.log(myState);
 *  console.log(myStateSync);
 * }, [myState]);
 *
 * IMPORTANT: use myStateSync only in handlers, never to render components. Changes on this value will not be tracked
 *  and will not trigger re-renders as expected.
 * NOTE: myStateSync doesn not need to be tracked as a dependency to handlers
 */
export function useSyncState<S>(initialState: S | (() => S)): [S, MutableRefObject<S>, Dispatch<S>] {
  const [state, setState] = useState<S>(initialState);
  const current = useRef<S>(state);
  const setSyncState = useCallback(
    function (newValue: S): void {
      current.current = newValue;
      setState(newValue);
    },
    [current]
  );
  return [state, current, setSyncState];
}

export const localizedDateFormatter = (value: any) => {
  if (!value) return '';
  const dt = DateTime.fromISO(value.toISOString?.() ?? value).setLocale(i18n.language ?? 'de');
  return dt.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
};

export function formatTime(date: Date): string {
  return new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

export const getDataGridLocaleText = () => {
  const dataGridLocales: Record<string, any> = {
    de: deDE,
    fr: frFR,
    en: enUS,
  };
  const lang = i18n.language;
  const baseLang = lang.split('-')[0];
  const locale = dataGridLocales[i18n.language] || dataGridLocales[baseLang];

  if (!locale) return deDE.components.MuiDataGrid.defaultProps.localeText;

  return locale.components.MuiDataGrid.defaultProps.localeText;
};

// Helper function to get nested object values
// e.g., get(obj, 'a.b.c') is equivalent to obj?.a?.b?.c
export function getNestedProperty(obj: any, path: string): any {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

export function getTimeDifference(targetDate: Date | undefined, now: Date) {
  if (!targetDate) return { key: 'activity.noActivity' };

  const diffInSeconds = Math.floor((now.getTime() - targetDate.getTime()) / 1000);

  if (diffInSeconds < 60) return { key: 'activity.justnow' };

  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return { key: 'activity.minuteAgo', count: minutes };
  }

  const hours = Math.floor(diffInSeconds / 3600);
  return { key: 'activity.hourAgo', count: hours };
}

export function getUserPathId(user?: { sub?: string; authId?: string }): string | undefined {
  return user?.sub || user?.authId;
}
